import React, { useEffect, useRef, useState } from 'react';
import {
  PermissionSyncStatus,
  useGetPermissionSyncStatusQuery,
  useGetUserProfileQuery,
} from 'src/generated/graphql';
import { LoadingStatus } from 'src/common/ui/StatusScreen';
import ProfileUnavailablePage from 'src/components/auth/ProfileUnavailablePage';
import PermissionsUnavailablePage from 'src/components/auth/PermissionsUnavailablePage';
import {
  ClientWaitClocks,
  decideBootstrapAction,
  hasRenderableCachedPermissions,
  PERMISSION_SYNC_POLL_INTERVAL_MS,
} from 'src/components/auth/permissionSyncDecision';
import { useTranslation } from 'src/i18n/shim/useTranslation';

/** Advice copy, NOT a threshold this component acts on — `PERMISSION_SYNC_WAIT_BUDGET_MS`, which
 * decides when the extended caption appears, is a different and much shorter figure. */
const SYNC_ESCALATION_ADVICE_MINUTES = 30;

/**
 * Bootstrap step that runs once authentication has succeeded and before any
 * page-level query is issued.
 *
 * It wraps the whole authenticated tree (see src/App.tsx), so nothing below it
 * mounts — and therefore nothing below it can issue a query — until the bootstrap
 * has finished. Wrapping the router rather than sitting inside it as a layout route
 * is what makes that structural: a route cannot be added outside the gate by
 * accident.
 *
 * Two steps. The second is CONDITIONAL, and the first decides whether it runs:
 *
 * 1. `getUserProfile`. It does two jobs. It has a backend side effect — it triggers
 *    the asynchronous permission-sync refresh for the caller — and it returns the
 *    caller's row, including the server-computed `hasFreshCachedPermissions`. The
 *    gate reads that field: it is the whole answer for a caller whose cache was synced
 *    recently enough to render from. The side effect is why the query uses
 *    `network-only`: serving it from the in-memory Apollo cache on a remount would
 *    skip the trigger, and the trigger is what refreshes a stale or empty cache.
 *
 * 2. `getPermissionSyncStatus`, polled — but only when step 1 reported no fresh
 *    cache. `skip` covers both conditions: it is skipped until step 1 has returned,
 *    which enforces the ordering (reading the status before the trigger has fired
 *    would only ever report the pre-trigger state), AND skipped outright when the
 *    profile already reports a fresh cache. Both queries read the same user row and
 *    the same `hasFreshCachedPermissions`, so on the fresh path the second round
 *    trip and its "checking your access" loader would add nothing —
 *    {@link ./permissionSyncDecision#hasRenderableCachedPermissions} is where that
 *    reasoning lives. Otherwise the poll runs unchanged and the loader rules in
 *    {@link ./permissionSyncDecision} turn each reading into render / wait / give up.
 *
 * The skip errs toward polling, not toward rendering: `hasFreshCachedPermissions` is
 * computed from the row as read BEFORE this call's trigger fires, and `lastSyncedAt`
 * only moves forward, so the answer can understate freshness but never overstate it.
 * A false negative costs one round trip. The one direction the skip does not protect
 * against is the answer AGEING: a cache that was fresh when the profile was computed
 * can cross the threshold moments later, and the skipped poll would have said so. The
 * window that buys is bounded by the round trip, which is the same tolerance the
 * threshold itself already has — so this is a reason not to shrink the threshold to
 * seconds, not a hole in the gate.
 *
 * Both failure branches render in place of the children, at whatever URL the user is
 * on: the URL is where the user meant to be, and reloading it re-runs both steps,
 * which is what makes "refresh this page to try again" true.
 */
const AppBootstrap: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const {
    data: profileData,
    loading: profileLoading,
    error: profileError,
  } = useGetUserProfileQuery({ fetchPolicy: 'network-only' });

  const profileReady = !profileError && !!profileData;

  // The profile's own answer to "was this cache synced recently enough to render
  // from?". When true the status query is never issued, so the gate has one round trip
  // instead of two.
  const cacheUsableFromProfile = hasRenderableCachedPermissions(
    profileData?.getUserProfile,
  );

  // Named once and read by both the query's `skip` and `settled` below, so the query
  // and the poll effect cannot disagree about whether a status read is coming. The
  // same hazard `statusUnavailable` addresses further down: a poll left running behind
  // a branch that has already decided what to render.
  const statusQuerySkipped = !profileReady || cacheUsableFromProfile;

  const {
    data: statusData,
    error: statusError,
    startPolling,
    stopPolling,
  } = useGetPermissionSyncStatusQuery({
    // Two reasons to skip, both of which make the read pointless rather than merely
    // early: step 1 has not returned (the trigger has not had its chance to fire), or
    // step 1 already reported a fresh cache (this query reads the same row and the
    // same field, so it could only repeat the answer).
    skip: statusQuerySkipped,
    // Every poll must reach the server. A cached read would return the same
    // answer forever and the loader would never exit — the same reason the
    // AppSync resolver must not be given a caching config.
    fetchPolicy: 'network-only',
  });

  // Forces a re-decision when a wait deadline passes. The decision is a function of
  // `Date.now()`, so without this the only thing that could end a wait is a poll
  // landing, which would round every window up to the next poll boundary — the FAILED
  // grace window would then be a multiple of the poll interval rather than the length
  // permissionSyncDecision specifies.
  const [deadlineTick, setDeadlineTick] = useState(0);

  // First-observation times for the two loader states with no server timestamp to
  // measure against (see permissionSyncDecision).
  //
  // Refs rather than state because they are inputs to the current decision, not
  // something to re-render on. Written during render, which is safe here because
  // every write is idempotent — set-once-if-unset, or clear — so StrictMode's
  // double-invoked render produces the same anchors as a single pass.
  const clocks = useRef<ClientWaitClocks>({
    waitWithoutServerStartSince: null,
    failedFirstSeenAt: null,
  });

  const snapshot = statusData?.getPermissionSyncStatus;
  const now = Date.now();

  if (snapshot && !snapshot.hasFreshCachedPermissions) {
    const isFailed = snapshot.syncStatus === PermissionSyncStatus.Failed;
    const hasServerRunStartTime =
      snapshot.syncStatus === PermissionSyncStatus.InProgress &&
      typeof snapshot.syncStartedAt === 'number';

    // A FAILED that flips away and back starts a new grace window: the second
    // FAILED belongs to a different run than the first.
    clocks.current.failedFirstSeenAt = isFailed
      ? (clocks.current.failedFirstSeenAt ?? now)
      : null;

    // Only a wait with no server start time gets a client clock; a run that reports
    // when it started is measured against that instead.
    clocks.current.waitWithoutServerStartSince =
      isFailed || hasServerRunStartTime
        ? clocks.current.waitWithoutServerStartSince
        : (clocks.current.waitWithoutServerStartSince ?? now);
  }

  const decision = snapshot
    ? decideBootstrapAction(snapshot, now, clocks.current)
    : null;

  // Logging is a side effect, so it belongs in an effect keyed on the error rather
  // than in the render body: the app is wrapped in StrictMode (see src/index.tsx),
  // which double-invokes render, and the component renders again on every query
  // state transition. Keyed this way a given failure is reported once instead of
  // once per render pass.
  useEffect(() => {
    if (profileError) {
      console.error('[AppBootstrap] getUserProfile failed', profileError);
    }
  }, [profileError]);

  useEffect(() => {
    if (statusError) {
      console.error(
        '[AppBootstrap] getPermissionSyncStatus failed',
        statusError,
      );
    }
  }, [statusError]);

  // A status read that failed before any snapshot landed is terminal, and naming it
  // once is the whole point: the render branch below shows the error page for exactly
  // this condition, and that page's only advice is to refresh. When the poll effect
  // and the render branch each carried their own idea of "finished", the poll went on
  // running forever behind a page telling the user that refreshing was the way out.
  const statusUnavailable = !!statusError && !decision;

  // Poll only while the answer can still change what the gate shows. Four ways to be
  // finished: the query is skipped, so there is no reading to wait for and none is
  // coming; RENDER, where a later background refresh is invisible by design; GIVE_UP,
  // where the recovery is a page refresh rather than more polling; and a first read
  // that failed outright, which shows that same "please refresh" page. WAIT_EXTENDED
  // deliberately keeps polling: it is the state where refreshing cannot help, so a poll
  // is the only thing that can end it. Apollo owns the interval timer and clears it
  // when the query unmounts, and the cleanup here stops it on every transition, so
  // there is no interval left running behind a settled decision.
  //
  // The skip case is listed first because it is the one that has no decision to read:
  // both states that skip — profile not ready, and a fresh cache reported by the
  // profile — leave `decision` null, and a null decision must not be mistaken for
  // "still waiting" by the poll effect.
  //
  // A failed poll is deliberately NOT settling once a snapshot HAS been read: a wait
  // spans dozens of polls, so treating one transient failure as terminal would strand
  // users on "please refresh" mid-sync. Keep polling and let the wait budget bound it
  // — if the failures persist, the deadline still produces row 5 rather than an
  // indefinite loader.
  const settled =
    statusQuerySkipped ||
    decision?.action === 'RENDER' ||
    decision?.action === 'GIVE_UP' ||
    statusUnavailable;

  useEffect(() => {
    if (settled) {
      stopPolling();
      return;
    }
    startPolling(PERMISSION_SYNC_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [settled, startPolling, stopPolling]);

  // One timer, for the current wait's deadline, replaced whenever the deadline moves
  // and cleared on unmount. Not an interval: a wait has exactly one moment at which
  // it expires, and re-deciding at any other time would produce the same answer.
  const reevaluateAt =
    decision?.action === 'WAIT' ? decision.reevaluateAt : null;
  useEffect(() => {
    if (reevaluateAt === null) {
      return;
    }
    const delay = Math.max(0, reevaluateAt - Date.now()) + 1;
    const timer = setTimeout(() => setDeadlineTick((tick) => tick + 1), delay);
    return () => clearTimeout(timer);
    // `deadlineTick` is a dependency so that a deadline which has already passed
    // (delay 0) cannot wedge: each tick re-runs the effect against a fresh decision.
  }, [reevaluateAt, deadlineTick]);

  if (profileLoading) {
    return <LoadingStatus>{t('auth-loading-profile')}</LoadingStatus>;
  }

  // `!profileData` shares the error branch rather than getting one of its own. The
  // gate reads the profile now — `hasFreshCachedPermissions` is what decides
  // whether step 2 runs at all — so resolving with no profile leaves the gate unable
  // to answer either of the questions it exists for: it has not confirmed the
  // permission-sync trigger fired, and it has no cache reading to act on. A settled
  // query under Apollo's default `errorPolicy: 'none'` is either an error or data, so
  // this should not happen; if it ever does, the gate must not let the app through.
  if (!profileReady) {
    return <ProfileUnavailablePage />;
  }

  // Fresh cache, straight from step 1. `getPermissionSyncStatus` is skipped in this
  // state, so there is no status error to fail closed on and no poll to stop — the
  // loader rules would return row 1 (RENDER) here anyway, one round trip and one
  // loader later.
  if (cacheUsableFromProfile) {
    return <>{children}</>;
  }

  // The status read itself failing is not one of the loader rules. Fail closed only while
  // there is no answer at all: without one the gate cannot know whether the caller
  // has permissions, and letting the app through would render permission-filtered
  // pages that silently come back empty. Once a snapshot has been read, a later poll
  // failing is transient — keep showing the decision that snapshot produced. Same
  // `statusUnavailable` the poll effect settles on, so the poll is already stopped by
  // the time this renders.
  if (statusUnavailable) {
    return <PermissionsUnavailablePage />;
  }

  if (!decision) {
    // First status read still in flight. Brief by construction: one request, not a
    // wait.
    return <LoadingStatus>{t('auth-checking-access')}</LoadingStatus>;
  }

  if (decision.action === 'GIVE_UP') {
    return <PermissionsUnavailablePage />;
  }

  if (decision.action === 'WAIT') {
    return <LoadingStatus>{t('auth-syncing-permissions')}</LoadingStatus>;
  }

  // Over budget but still running. Not an error: polling continues, so this resolves
  // itself if the run finishes and turns into the FAILED case if it does not. The one
  // loader copy that names a human, because it is the one state a user cannot end
  // themselves — refreshing returns this same reading while the run holds the
  // singleton key.
  if (decision.action === 'WAIT_EXTENDED') {
    return (
      <LoadingStatus>
        {t('auth-syncing-permissions-extended', {
          minutes: SYNC_ESCALATION_ADVICE_MINUTES,
        })}
      </LoadingStatus>
    );
  }

  return <>{children}</>;
};

export default AppBootstrap;
